import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { debounce } from 'lodash';
import { Badge, BlockStack, Card, FormLayout, useIndexResourceState, InlineStack, Layout, Page, Text, TextField, Toast, Divider, Select, Box, Frame, Button } from '@shopify/polaris';
import { useDispatch, useSelector } from 'react-redux';
import { getAllUserOnboarding } from '../redux/userOnboarding/slice';
import useOnboardingRedirect from '../redux/userOnBoardingRedirect';
import { getCollectionAsync, getProductAsync, getProductVariantAsync } from '../redux/product';
import { getAllProducts } from '../redux/product/slice';
import { createRuleAsync, getRuleByIdAsync } from '../redux/rule';
import { getAllRule } from '../redux/rule/slice';
import { getMetafieldsAsync } from '../redux/setting';
import { getAllSetting } from '../redux/setting/slice';
import Toggle from '../components/common/Toggle';
import CollectionTable from '../components/collection/CollectionTable';
import ModalComponent from '../components/common/Modal';
import Placeholder from '../components/common/Placeholder';
import ChooseSection from '../components/rule/ChooseSection';
import ProductTable from '../components/configure-products/ProductTable';
import ProductVariantTable from '../components/configure-products/ProductVariantTable';
import LoadingComponent from '../components/common/Loading';
import { FORMULA_TYPES, FORMULA_VARIABLES, NUMERIC_METAFIELD_TYPES, formulaVariablesHelpText, limit, ROUNDING_RULES, toFormulaVariable, validateCustomFormula } from '../utils';
import '../index.css';

const CreateRulePage = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { userOnboardingData } = useSelector(getAllUserOnboarding);
    const { collections, collectionPageInfo, isCollectionLoading, totalCollectionCount, products, pageInfo, isProductLoading, totalProductsCount, productVariants, isProductVariantLoading, productVariantPageInfo, totalProductVariantsCount } = useSelector(getAllProducts);
    const { isRuleLoading, rule, isRuleByIdLoading } = useSelector(getAllRule);
    const { metafieldsData } = useSelector(getAllSetting);
    const [message, setMessage] = useState('');
    const [active, setActive] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [isModalObj, setIsModalObj] = useState({
        open: false,
        type: ''
    });
    const [selectedCollection, setSelectedCollection] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState([]);
    const [selectedProductVariant, setSelectedProductVariant] = useState([]);
    const [formData, setFormData] = useState({
        ruleName: '',
        description: '',
        status: 'active',
        autoApply: false,
        formulaType: 'fixed',
        formula: '(base + makingCharge) * weight',
        roundingRule: 'nearest_1'
    });

    /* The variables this store may use: the standard set plus a name for each extra
       numeric metafield it has defined, so an enabled store can price on its own
       fields while every other store sees exactly the standard list. */
    const formulaVariables = useMemo(() => {
        const custom = (metafieldsData || [])
            .filter((metafield) => NUMERIC_METAFIELD_TYPES.includes(metafield?.type))
            .map((metafield) => toFormulaVariable(metafield?.key))
            .filter(Boolean);
        return [...new Set([...FORMULA_VARIABLES, ...custom])];
    }, [metafieldsData]);

    /* Only custom formulas are editable, so the built-in ones never need validating. */
    const formulaError = useMemo(
        () => (formData.formulaType === 'custom' ? validateCustomFormula(formData.formula, formulaVariables) : ''),
        [formData.formulaType, formData.formula, formulaVariables]
    );

    /* A rule saved before this validation existed must not trap the merchant on this
       page when they only came to rename it, so an untouched formula warns instead of
       blocking. The moment they edit the formula themselves it has to be valid again. */
    const isUntouchedLegacyFormula = Boolean(id) && rule?.formula?.expression === formData.formula;
    const blockSave = formulaError !== '' && !isUntouchedLegacyFormula;
    const formulaErrorMessage = formulaError && isUntouchedLegacyFormula
        ? `${formulaError} You can still save your other changes, but prices will not update until this formula is fixed.`
        : formulaError;

    const { selectedResources, allResourcesSelected, handleSelectionChange, removeSelectedResources } = useIndexResourceState(collections);
    const { selectedResources: selectedProductResources, allResourcesSelected: allProductResourcesSelected, handleSelectionChange: handleProductSelectionChange, removeSelectedResources: removeProductSelectedResources } = useIndexResourceState(products);
    const { selectedResources: selectedProductVariantResources, allResourcesSelected: allProductVariantResourcesSelected, handleSelectionChange: handleProductVariantSelectionChange, removeSelectedResources: removeProductVariantSelectedResources } = useIndexResourceState(productVariants);
    useOnboardingRedirect(userOnboardingData);

    const debouncedSearch = useMemo(
        () =>
            debounce(async (pagination) => {
                if (isModalObj.open && isModalObj.type === 'collections') {
                    dispatch(getCollectionAsync({
                        payload: pagination
                    }));
                } else if (isModalObj.open && isModalObj.type === 'products') {
                    dispatch(getProductAsync({
                        payload: pagination
                    }));
                } else if (isModalObj.open && isModalObj.type === 'productVariants') {
                    dispatch(getProductVariantAsync({
                        id: selectedProduct[0],
                        payload: pagination
                    }));
                }
            }, 300),
        [dispatch, isModalObj]
    );

    const toggleActive = useCallback((message) => {
        setMessage(message);
        setActive((active) => !active);
    }, []);

    const toastMarkup = active ? (
        <Toast duration={2000} content={message} onDismiss={() => toggleActive('')} />
    ) : null;

    useEffect(() => {
        handlePaginationChange({
            after: '',
            before: '',
            query: isModalObj.type === 'products' ? `collection_id:${selectedCollection[0]?.split('/')?.pop()}` : isModalObj.type === 'productVariants' ? `product_id:${selectedProduct[0]?.split('/')?.pop()}` : searchValue,
            limit: limit
        });
    }, [searchValue, isModalObj]);

    useEffect(() => {
        if (id) {
            dispatch(getRuleByIdAsync({
                payload: { id }
            }));
        }
    }, [id, dispatch]);

    /* Needed for the formula variable list, which includes any metafield this store
       has defined for itself on top of the standard ones. */
    useEffect(() => {
        dispatch(getMetafieldsAsync());
    }, [dispatch]);

    useEffect(() => {
        if (rule) {
            setFormData({
                ruleName: rule.name,
                description: rule.description,
                status: rule.status,
                formulaType: rule.formulaType,
                autoApply: rule.settings.auto_apply,
                formula: rule.formula.expression,
                roundingRule: rule.formula.rounding
            });
            const collections = rule?.scope?.map((scope) => scope?.collectionsId).filter(Boolean) || [];
            if (collections?.length > 0) {
                handleSelectionUpdate('collection', rule?.scope?.map((scope) => scope?.collectionsId) || []);
            }
            const products = rule?.scope?.map((scope) => scope?.productIds).filter(Boolean) || [];
            if (products?.length > 0) {
                handleSelectionUpdate('product', rule?.scope?.map((scope) => scope?.productIds) || []);
            }
            if (rule.scope[0]?.variantIDs?.length > 0) {
                handleSelectionUpdate('productVariant', rule.scope[0]?.variantIDs || []);
            }
        }
    }, [rule]);

    const handleModalOpen = (type) => setIsModalObj({ open: true, type: type });

    const handleModalClose = () => setIsModalObj({ open: false, type: '' });

    const handleCollectionAdd = () => {
        setSelectedCollection(selectedResources);
        handleModalClose();
    };

    const handlePaginationChange = (pagination) => {
        debouncedSearch(pagination);
    };

    const handleFiltersQueryChange = useCallback(
        (value) => setSearchValue(value),
        [],
    );

    const handleQueryValueRemove = useCallback(() => setSearchValue(''), []);

    const handleFormChange = (name, value) => {
        switch (name) {
            case 'ruleName':
                value = value.trimStart()
                setFormData({ ...formData, ruleName: value });
                break;
            case 'description':
                setFormData({ ...formData, description: value });
                break;
            case 'status':
                setFormData({ ...formData, status: value });
                break;
            case 'formulaType':
                if (value === 'percentage') {
                    setFormData({ ...formData, formulaType: value, formula: '(basePrice + weight) + ((basePrice + weight) * makingCharge / 100)' });
                } else {
                    setFormData({ ...formData, formulaType: value, formula: '(base + makingCharge) * weight' });
                }
                break;
            case 'autoApply':
                setFormData({ ...formData, autoApply: value });
                break;
            case 'formula':
                setFormData({ ...formData, formula: value });
                break;
            case 'roundingRule':
                setFormData({ ...formData, roundingRule: value });
                break;
            default:
                break;
        }
    };

    const handleBack = () => {
        navigate('/rules');
    };

    const handleCreateRule = () => {
        const data = {
            ...(id && { updateId: id }),
            name: formData.ruleName,
            description: formData.description,
            status: formData.status,
            formulaType: formData.formulaType,
            formula: {
                expression: formData.formula,
                rounding: formData.roundingRule
            },
            settings: {
                auto_apply: formData.autoApply,
                last_applied_at: new Date()
            },
            scope: [
                {
                    collectionsId: selectedCollection?.length > 0 ? selectedCollection[0]?.toString() : '',
                    productIds: selectedProduct?.length > 0 ? selectedProduct[0]?.toString() : '',
                    variantIDs: selectedProductVariant?.length > 0 ? selectedProductVariant : []
                }
            ]
        }
        dispatch(createRuleAsync({
            callback: handleCallBack,
            payload: data
        }));
    };

    const handleCallBack = (message) => {
        toggleActive(message);
        if (!id) {
            handleReset();
        }
    };

    const handleReset = () => {
        setFormData({
            ruleName: '',
            description: '',
            status: 'active',
            autoApply: false,
            formulaType: 'fixed',
            formula: 'Base Price + (Weight * Making Charge)',
            roundingRule: 'nearest_1'
        });
        setIsModalObj({ open: false, type: '' });
        setSelectedCollection([]);
        setSelectedProduct([]);
        setSelectedProductVariant([]);
        setTimeout(() => {
            handleBack();
        }, 1000);
    };

    const handleCollectionRemove = (option) => {
        removeSelectedResources([option]);
        setSelectedCollection(selectedCollection.filter((collection) => collection !== option));
        setSelectedProduct([]);
        setSelectedProductVariant([]);
    };

    const handleProductRemove = (option) => {
        removeProductSelectedResources([option]);
        setSelectedProduct(selectedProduct.filter((product) => product !== option));
        setSelectedProductVariant([]);
    };

    const handleProductVariantRemove = (option) => {
        removeProductVariantSelectedResources([option]);
        setSelectedProductVariant(selectedProductVariant.filter((productVariant) => productVariant !== option));
    };

    const handleProductAdd = () => {
        setSelectedProduct(selectedProductResources);
        handleModalClose();
    };

    const handleProductVariantAdd = () => {
        setSelectedProductVariant(selectedProductVariantResources);
        handleModalClose();
    };

    const handleSelectionUpdate = (type, ids) => {
        if (type === 'collection') {
            handleSelectionChange('single', true, ids[0]);
            setSelectedCollection(ids);
        } else if (type === 'product') {
            handleProductSelectionChange('single', true, ids[0]);
            setSelectedProduct(ids);
        } else if (type === 'productVariant') {
            setSelectedProductVariant(ids);
            if (ids.length > 0) {
                for (const id of ids) {
                    handleProductVariantSelectionChange('single', true, id);
                }
            }
        }
    };

    const spacingMarkup = () => {
        return (
            <React.Fragment>
                <Placeholder padding='5px var(--p-space-100)' height='2px'/>
                <Divider borderColor='border' borderWidth='0165' />
                <Placeholder padding='5px var(--p-space-100)' height='2px'/>
            </React.Fragment>
        );
    };

    if (isRuleByIdLoading) {
        return <LoadingComponent />;
    }

    return (
        <Page
            backAction={{ content: 'Back', onAction: handleBack }}
            title={
                <BlockStack gap='100'>
                    <Text variant='headingLg' as='h1'>
                        {id ? 'Edit Rule' : 'Create Rule'}
                    </Text>
                    <Text variant='bodySm' tone='subdued'>
                        {id ? 'Edit the rule to automate your pricing calculations.' : 'Create a new rule to automate your pricing calculations.'}
                    </Text>
                </BlockStack>
            }
            primaryAction={{ content: id ? 'Update' : 'Save', disabled: formData?.ruleName === '' || blockSave, loading: isRuleLoading, onAction: handleCreateRule }}
        >
            <Layout>
                <Layout.Section>
                    <Card>
                        <FormLayout>
                            <BlockStack gap="200">
                                <InlineStack align="space-between" blockAlign="start">
                                    <Box>
                                        <Text variant="headingSm" as="span">Rule Name</Text>
                                        <Text variant='bodySm' tone='subdued'>
                                            Enter the name of the rule.
                                        </Text>
                                    </Box>
                                    <InlineStack align="start" gap="200" blockAlign="center">
                                        <Text variant="bodySm" as="span">
                                            Status
                                        </Text>
                                        <Badge tone={formData.status === 'active' ? 'success' : 'attention'}>
                                            {formData.status === 'active' ? 'Enabled' : 'Disabled'}
                                        </Badge>
                                        &nbsp;&nbsp;&nbsp;
                                        <Toggle
                                            selected={formData.status === 'active'}
                                            ariaLabel="Toggle status"
                                            onClick={() => handleFormChange('status', formData.status === 'active' ? 'inactive' : 'active')}
                                        />
                                    </InlineStack>
                                </InlineStack>
                                <TextField
                                    value={formData.ruleName}
                                    placeholder='Enter rule name'
                                    requiredIndicator
                                    onChange={(value) => handleFormChange('ruleName', value)}
                                />
                            </BlockStack>
                            <BlockStack gap="200">
                                <Text variant="headingSm" as="span">Description</Text>
                                <Text variant='bodySm' tone='subdued'>
                                    Enter the description of the rule.
                                </Text>
                                <TextField
                                    value={formData.description}
                                    placeholder='Enter description'
                                    requiredIndicator
                                    multiline={3}
                                    onChange={(value) => handleFormChange('description', value)}
                                />
                            </BlockStack>
                            <BlockStack gap="200">
                                <InlineStack align="start" gap="200" blockAlign="center">
                                    <Text variant="headingSm" as="span">
                                        Auto Apply Rule
                                    </Text>
                                    <Badge tone={formData.autoApply ? 'success' : 'attention'}>
                                        {formData.autoApply ? 'Enabled' : 'Disabled'}
                                    </Badge>
                                    &nbsp;&nbsp;&nbsp;
                                    <Toggle
                                        selected={formData.autoApply}
                                        ariaLabel="Toggle auto price update"
                                        onClick={() => handleFormChange('autoApply', formData.autoApply ? false : true)}
                                    />
                                </InlineStack>
                                <Text variant="bodySm" tone='subdued'>
                                    When enabled, the rule will be applied to the products automatically.
                                </Text>
                            </BlockStack>
                        </FormLayout>
                    </Card>
                </Layout.Section>
                <Layout.Section>
                    <Card>
                        <ChooseSection
                            selectedIds={selectedCollection}
                            handleModalOpen={handleModalOpen}
                            handleIdRemove={handleCollectionRemove}
                            title="Choose Collection"
                            titleHelp="Choose the collection to apply the rule. If you don't choose any collection, the rule will be applied to all products."
                            createButtonText="Choose Collection"
                            modalType="collections"
                        />
                        {selectedCollection?.length > 0 && spacingMarkup()}
                        {selectedCollection?.length > 0 &&
                            <ChooseSection
                                selectedIds={selectedProduct}
                                handleModalOpen={handleModalOpen}
                                handleIdRemove={handleProductRemove}
                                title="Choose Product"
                                titleHelp="Choose the product to apply the rule. If you don't choose any product, the rule will be applied to all products."
                                createButtonText="Choose Product"
                                modalType="products"
                            />
                        }
                        {selectedProduct?.length > 0 && spacingMarkup()}
                        {selectedProduct?.length > 0 &&
                            <ChooseSection
                                selectedIds={selectedProductVariant}
                                handleModalOpen={handleModalOpen}
                                handleIdRemove={handleProductVariantRemove}
                                title="Choose Variant"
                                titleHelp="Choose the variant to apply the rule. If you don't choose any variant, the rule will be applied to all variants."
                                createButtonText="Choose Variant"
                                modalType="productVariants"
                            />
                        }
                    </Card>
                </Layout.Section>
                <Layout.Section>
                    <Card>
                        <FormLayout>
                            <FormLayout.Group condensed>
                                <BlockStack gap="200">
                                    <Text variant="headingSm" as="span">
                                        Formula Type
                                    </Text>
                                    <Text variant="bodySm" tone='subdued'>
                                        Selected default formula type for the formula definition.
                                    </Text>
                                    <Select
                                        options={FORMULA_TYPES}
                                        onChange={(value) => handleFormChange('formulaType', value)}
                                        value={formData.formulaType}
                                        readOnly
                                    />
                                </BlockStack>
                                <BlockStack gap="200">
                                    <Text variant="headingSm" as="span">
                                        Rounding Rule
                                    </Text>
                                    <Text variant="bodySm" tone='subdued'>
                                        Selected default rounding rule for the formula definition.
                                    </Text>
                                    <Select
                                        options={ROUNDING_RULES}
                                        onChange={(value) => handleFormChange('roundingRule', value)}
                                        value={formData.roundingRule}
                                    />
                                </BlockStack>
                            </FormLayout.Group>
                            <BlockStack gap="200">
                                <Text variant="headingSm" as="span">
                                    Formula Logic for ${formData.formulaType}
                                </Text>
                                <Text variant="bodySm" tone='subdued'>
                                    here we use ${formData.formulaType} formula to calculate the price.
                                </Text>
                                <TextField
                                    value={formData.formula}
                                    multiline={2}
                                    autoComplete="off"
                                    onChange={(value) => handleFormChange('formula', value)}
                                    readOnly={formData.formulaType !== 'custom'}
                                    helpText={formData.formulaType === 'custom' ? formulaVariablesHelpText(formulaVariables) : undefined}
                                    error={formulaErrorMessage}
                                />
                            </BlockStack>
                        </FormLayout>
                    </Card>
                </Layout.Section>
            </Layout>
            <Placeholder height='10px' />
            {toastMarkup}
            <ModalComponent
                title="Choose Collection"
                open={isModalObj.open && isModalObj.type === 'collections'}
                handleModalClose={() => handleModalClose({ open: false, type: '' })}
                primaryAction={{
                    content: 'Add Collection',
                    onAction: handleCollectionAdd
                }}
            >
                <CollectionTable
                    collections={collections}
                    pageInfo={collectionPageInfo}
                    totalCollectionsCount={totalCollectionCount}
                    onPaginationChange={handlePaginationChange}
                    isCollectionLoading={isCollectionLoading}
                    searchValue={searchValue}
                    handleFiltersQueryChange={handleFiltersQueryChange}
                    handleQueryValueRemove={handleQueryValueRemove}
                    selectedResources={selectedResources}
                    allResourcesSelected={allResourcesSelected}
                    handleSelectionChange={handleSelectionChange}
                />
            </ModalComponent>
            <ModalComponent
                title="Choose Product"
                open={isModalObj.open && isModalObj.type === 'products'}
                handleModalClose={() => handleModalClose({ open: false, type: '' })}
                primaryAction={{
                    content: 'Add Product',
                    onAction: handleProductAdd
                }}
            >
                <ProductTable
                    products={products}
                    pageInfo={pageInfo}
                    totalProductsCount={totalProductsCount}
                    onPaginationChange={handlePaginationChange}
                    isProductLoading={isProductLoading}
                    searchValue={searchValue}
                    handleFiltersQueryChange={handleFiltersQueryChange}
                    handleQueryValueRemove={handleQueryValueRemove}
                    selectedResources={selectedProductResources}
                    allResourcesSelected={allProductResourcesSelected}
                    handleSelectionChange={handleProductSelectionChange}
                    collectionId={selectedCollection[0]?.split('/')?.pop()}
                />
            </ModalComponent>
            <ModalComponent
                title="Choose Product Variant"
                open={isModalObj.open && isModalObj.type === 'productVariants'}
                handleModalClose={() => handleModalClose({ open: false, type: '' })}
                primaryAction={{
                    content: 'Add Product Variant',
                    onAction: handleProductVariantAdd
                }}
            >
                <ProductVariantTable
                    productVariants={productVariants}
                    productVariantPageInfo={productVariantPageInfo}
                    totalProductVariantsCount={totalProductVariantsCount}
                    onPaginationChange={handlePaginationChange}
                    isProductVariantLoading={isProductVariantLoading}
                    selectedResources={selectedProductVariantResources}
                    allResourcesSelected={allProductVariantResourcesSelected}
                    handleSelectionChange={handleProductVariantSelectionChange}
                    productId={selectedProduct[0]?.split('/')?.pop()}
                />
            </ModalComponent>
        </Page>
    );
};

export default CreateRulePage;